import React from 'react';
/**
 * Hook para gerenciamento de modo de edição da contagem
 *
 * Responsabilidades:
 * - Controle de edit mode
 * - Tracking de campos modificados
 * - Original data para comparação
 * - Discard/Save changes helpers
 */
import { useState, useCallback, useEffect } from 'react';

export interface UseContagemEditReturn {
  // Estado de edição
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  isReadOnly: boolean;

  // Campos modificados
  modifiedFields: Set<string>;
  setModifiedFields: React.Dispatch<React.SetStateAction<Set<string>>>;

  // Dados originais para comparação
  originalFormData: any;
  setOriginalFormData: React.Dispatch<React.SetStateAction<any>>;

  // Helpers
  trackFieldModification: (field: string, currentValue: any, originalValue: any) => void;
  trackArrayModification: (fieldName: string, newArray: any[], originalArray: any[]) => void;
  clearModifiedFields: () => void;
  hasModifications: boolean;

  // Legacy tracking
  legacyLocationId: number | null;
  setLegacyLocationId: React.Dispatch<React.SetStateAction<number | null>>;
  originalHospitalId: number | null;
  setOriginalHospitalId: React.Dispatch<React.SetStateAction<number | null>>;
}

export function useContagemEdit(isNewContagem: boolean): UseContagemEditReturn {
  // Edit mode control
  const [isEditMode, setIsEditMode] = useState(false);
  const isReadOnly = !isNewContagem && !isEditMode;

  // Modified fields tracking
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const [originalFormData, setOriginalFormData] = useState<any>(null);

  // Legacy field tracking (for hospital/location restrictions)
  const [legacyLocationId, setLegacyLocationId] = useState<number | null>(null);
  const [originalHospitalId, setOriginalHospitalId] = useState<number | null>(null);

  // Track single field modification
  const trackFieldModification = useCallback((
    field: string,
    currentValue: any,
    originalValue: any
  ) => {
    if (!isEditMode || !originalFormData) return;

    const isModified = currentValue !== originalValue;
    setModifiedFields(prev => {
      const newSet = new Set(prev);
      if (isModified) {
        newSet.add(field);
      } else {
        newSet.delete(field);
      }
      return newSet;
    });
  }, [isEditMode, originalFormData]);

  // Track array modifications (for auditores and locais lists)
  const trackArrayModification = useCallback((
    fieldName: string,
    newArray: any[],
    originalArray: any[]
  ) => {
    if (!isEditMode || !originalFormData) return;

    // Compare arrays by serializing IDs to JSON
    const newIds = JSON.stringify(newArray.map(item => item.id).sort());
    const originalIds = JSON.stringify(originalArray.map(item => item.id).sort());
    const isModified = newIds !== originalIds;

    setModifiedFields(prev => {
      const newSet = new Set(prev);
      if (isModified) {
        newSet.add(fieldName);
      } else {
        newSet.delete(fieldName);
      }
      return newSet;
    });
  }, [isEditMode, originalFormData]);

  // Clear all modifications
  const clearModifiedFields = useCallback(() => {
    setModifiedFields(new Set());
    setOriginalFormData(null);
  }, []);

  // Check if there are modifications
  const hasModifications = modifiedFields.size > 0;

  // Clean up when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      setOriginalFormData(null);
      setModifiedFields(new Set());
    }
  }, [isEditMode]);

  return {
    // Estado de edição
    isEditMode,
    setIsEditMode,
    isReadOnly,

    // Campos modificados
    modifiedFields,
    setModifiedFields,

    // Dados originais
    originalFormData,
    setOriginalFormData,

    // Helpers
    trackFieldModification,
    trackArrayModification,
    clearModifiedFields,
    hasModifications,

    // Legacy tracking
    legacyLocationId,
    setLegacyLocationId,
    originalHospitalId,
    setOriginalHospitalId,
  };
}
